"use client";

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { Input } from "@/components/forms";
import { useDropdownFloating } from "@/components/forms/useDropdownFloating";
import { Typography } from "@/components/common/ui/Typography";
import { AIGeneratedBadge } from "@/components/forms/AIGeneratedBadge";
import { usePropertyClassCodesQuery } from "@/features/submissions/api/queries/usePropertyClassCodesQuery";
import { FloatingPortal } from "@floating-ui/react";

interface PropertyClassCodeSelectProps {
  value: string;
  description?: string | null;
  onChange: (code: string, description: string) => void;
  /**
   * When ``true``, renders the AI sparkle next to the label / value to flag
   * the code as ingestion-time inferred. Disappears on user override.
   */
  inferred?: boolean;
  inferredReason?: string;
  required?: boolean;
  error?: string;
  label?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  onSourceClick?: () => void;
}

export function PropertyClassCodeSelect({
  value,
  description,
  onChange,
  inferred,
  inferredReason,
  required,
  error,
  label = "Property Class Code",
  autoFocus,
  disabled,
  onSourceClick,
}: PropertyClassCodeSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState(
    () => (value && description ? `${value} — ${description}` : value),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: catalog } = usePropertyClassCodesQuery(true);

  const formatSelected = useCallback(
    (code: string) => {
      if (!code) return "";
      const match = catalog?.find((cc) => cc.code === code);
      const selectedDescription = match?.description ?? description;
      return selectedDescription ? `${code} — ${selectedDescription}` : code;
    },
    [catalog, description],
  );

  const { refs, floatingStyle, getFloatingProps } = useDropdownFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) setSearch(formatSelected(value));
    },
    minWidth: 360,
  });

  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    refs.setReference(node);
  }, [refs]);

  useEffect(() => {
    if (!isOpen) {
      // Keep the editable search text aligned with controlled value changes.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSearch(formatSelected(value));
    }
  }, [value, isOpen, formatSelected]);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    // When showing the formatted selected value ("code — desc"), don't filter
    // — opening the dropdown should show everything until the user types.
    const formatted = formatSelected(value);
    const query =
      search && search !== formatted ? search.toLowerCase() : "";
    return catalog
      .filter(
        (cc) =>
          !query ||
          cc.code.toLowerCase().includes(query) ||
          cc.description.toLowerCase().includes(query),
      )
      .slice(0, 12);
  }, [catalog, search, value, formatSelected]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const dropdown =
    isOpen && filtered.length > 0
      ? (
        <FloatingPortal>
          <div
            ref={(node) => refs.setFloating(node)}
            style={floatingStyle}
            {...getFloatingProps()}
            className="overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg"
          >
            {filtered.map((cc) => (
              <button
                key={cc.code}
                type="button"
                onClick={() => {
                  onChange(cc.code, cc.description);
                  setSearch(`${cc.code} — ${cc.description}`);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm hover:bg-navigator-primary/5 transition-colors",
                  cc.code === value && "bg-navigator-primary/10 font-medium",
                )}
              >
                <span className="font-medium text-gray-900">{cc.code}</span>
                <Typography
                  as="span"
                  variant="small"
                  color="light"
                  className="ml-2"
                >
                  {cc.description}
                </Typography>
              </button>
            ))}
          </div>
        </FloatingPortal>
        )
      : null;

  return (
    <div className="relative w-full">
      <div className="flex items-center gap-1.5 mb-1">
        {onSourceClick ? (
          <button
            type="button"
            onClick={onSourceClick}
            className="text-left text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline"
            aria-label={`View source for ${label}`}
          >
            {label}
            {required ? <span className="text-red-500"> *</span> : null}
          </button>
        ) : (
          <Typography as="label" variant="small" weight="medium" color="default">
            {label}
            {required ? <span className="text-red-500"> *</span> : null}
          </Typography>
        )}
        {inferred ? (
          <AIGeneratedBadge size="sm" reason={inferredReason} />
        ) : null}
      </div>
      <Input
        ref={setInputRef}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Search property class code..."
        leftIcon={<Search className="h-3.5 w-3.5" />}
        rightIcon={<ChevronDown className="h-3.5 w-3.5" />}
        error={error}
        disabled={disabled}
      />
      {dropdown}
    </div>
  );
}
